import { useState, useEffect } from 'react'
import { X, FileArchive, Folder, AlertCircle, Loader2 } from 'lucide-react'
import { api } from '../utils/api'
import { useAuthStore } from '../stores/authStore'
import { useToastStore } from '../stores/toastStore'

interface UnzipModalProps {
  isOpen: boolean
  onClose: () => void
  file: any // File object with path, name, etc
  onSuccess?: () => void
}

export function UnzipModal({ isOpen, onClose, file, onSuccess }: UnzipModalProps) {
  const [targetFolder, setTargetFolder] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { token, csrfToken } = useAuthStore()
  const { addToast } = useToastStore()

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && file) {
      // Default target folder: same directory as ZIP file
      const filePath = file.path || ''
      const lastSlash = filePath.lastIndexOf('/')
      const parentFolder = lastSlash > 0 ? filePath.substring(0, lastSlash) : ''
      setTargetFolder(parentFolder)
      setError(null)
    }
  }, [isOpen, file])

  if (!isOpen || !file) return null

  const handleExtract = async () => {
    if (!token) {
      setError('Not authenticated')
      return
    }

    try {
      setIsExtracting(true)
      setError(null)

      console.log('📦 [UnzipModal] Extracting:', {
        filePath: file.path,
        targetFolder
      })

      // ✅ Use api.unzipFile method
      const response = await api.unzipFile(
        file.path,        // Full path to ZIP file
        targetFolder,     // Target extraction folder
        token,
        csrfToken || undefined
      )

      if (!response.success) {
        throw new Error(response.error || 'Extraction failed')
      }

      console.log('✅ [UnzipModal] Success:', response.data)

      addToast({
        type: 'success',
        message: `Successfully extracted ${response.data.count} files from ${file.name}`
      })

      onSuccess?.()
      onClose()

    } catch (err: any) {
      console.error('❌ [UnzipModal] Error:', err)
      
      let errorMessage = 'Failed to extract ZIP file'
      
      if (err.message) {
        errorMessage = err.message
      } else if (err.error) {
        errorMessage = err.error
      }
      
      setError(errorMessage)
      
      addToast({
        type: 'error',
        message: errorMessage
      })
    } finally {
      setIsExtracting(false)
    }
  }

  const handleClose = () => {
    if (!isExtracting) {
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-dark-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-500/20">
              <FileArchive className="h-5 w-5 text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Extract ZIP File</h2>
              <p className="text-sm text-dark-400">{file.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isExtracting}
            className="rounded-lg p-2 text-dark-400 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-400">Extraction Failed</p>
                <p className="text-sm text-red-300/80 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Target Folder Input */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              <Folder className="inline h-4 w-4 mr-1" />
              Extract to folder
            </label>
            <input
              type="text"
              value={targetFolder}
              onChange={(e) => setTargetFolder(e.target.value)}
              placeholder="Leave empty for root folder"
              disabled={isExtracting}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-dark-400 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <p className="text-xs text-dark-400 mt-2">
              Files will be extracted to: <span className="text-white font-mono">
                {targetFolder || '/'}/{file.name.replace('.zip', '')}
              </span>
            </p>
          </div>

          {/* Info Box */}
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-400">What happens next?</p>
                <ul className="text-sm text-blue-300/80 mt-2 space-y-1">
                  <li>• ZIP file will be extracted to the target folder</li>
                  <li>• Original ZIP file will remain intact</li>
                  <li>• Folder structure inside ZIP will be preserved</li>
                  <li>• All files will be uploaded to R2 storage</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-white/10 p-6">
          <button
            onClick={handleClose}
            disabled={isExtracting}
            className="rounded-lg border border-white/10 bg-white/5 px-6 py-2.5 font-medium text-white transition-all hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleExtract}
            disabled={isExtracting}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-6 py-2.5 font-semibold text-white transition-all hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExtracting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <FileArchive className="h-4 w-4" />
                Extract Files
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}